import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/dashboard/Header";
import { StatCards } from "@/components/dashboard/StatCards";
import { Filters, EMPTY_FILTERS } from "@/components/dashboard/Filters";
import { TicketTable } from "@/components/dashboard/TicketTable";
import { TicketDrawer } from "@/components/dashboard/TicketDrawer";
import { AttentionPanel } from "@/components/dashboard/AttentionPanel";
import { Pagination } from "@/components/dashboard/Pagination";
import { MyPerformance } from "@/components/dashboard/MyPerformance";
import { BulkAssignBar } from "@/components/dashboard/BulkAssignBar";
import { OfflineLeads } from "@/components/dashboard/OfflineLeads";
import { DistributeButton } from "@/components/dashboard/DistributeButton";

const PAGE_SIZE = 25;

function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export default function Dashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("all");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [range, setRange] = useState("today");
  const [picked, setPicked] = useState(() => new Set());
  const isAdmin = user.role === "admin";
  const togglePick = (id) => setPicked((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const toggleAll = (ids, on) => setPicked((s) => {
    const n = new Set(s);
    ids.forEach((id) => (on ? n.add(id) : n.delete(id)));
    return n;
  });
  const search = useDebounced(filters.search);
  const ticketNo = /^\s*(?:vs-?)?\d{1,6}\s*$/i.test(search) && /\d{2,}/.test(search) ? search.trim() : null;
  const { data: exact, isError: exactMissing } = useQuery({
    queryKey: ["ticket-by-number", ticketNo],
    queryFn: () => api.get(`/tickets/by-number/${encodeURIComponent(ticketNo)}`).then((r) => r.data),
    enabled: Boolean(ticketNo),
    retry: false,
  });

  const params = useMemo(() => {
    const p = { ...filters, search };
    if (tab === "mine") p.mine = true;
    return Object.fromEntries(Object.entries(p).filter(([, v]) => v !== "" && v !== false));
  }, [filters, search, tab]);
  const paramsKey = JSON.stringify(params);
  useEffect(() => setPage(1), [paramsKey]);

  const { data: config } = useQuery({ queryKey: ["form-config"], queryFn: () => api.get("/form-config").then((r) => r.data) });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: () => api.get("/tickets/stats").then((r) => r.data), refetchInterval: 60000 });
  const { data: pageData, isLoading } = useQuery({
    queryKey: ["tickets", params, page],
    queryFn: () => api.get("/tickets", { params: { ...params, page, page_size: PAGE_SIZE } }).then((r) => r.data),
    refetchInterval: 60000,
    placeholderData: (prev) => prev,
  });
  const tickets = pageData?.items || [];

  const assign = useMutation({
    mutationFn: (t) => api.post(`/tickets/${t.id}/assign`).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`${data.ticket_id} is now yours`);
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["my-performance"] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/tickets/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tickets-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const tabCls = (active) =>
    `rounded-t-xl border-2 border-b-0 px-5 py-2.5 font-display text-base font-bold transition-colors ${active ? "border-black bg-white" : "border-transparent bg-transparent text-zinc-500 hover:text-black"}`;

  return (
    <div className="min-h-screen bg-[#FFFBEB] grain">
      <Header />
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6">
        <div className="fade-up">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-600">Good to see you, {user.name.split(" ")[0]}</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Ticket queue</h1>
        </div>

        <AttentionPanel onOpenTicket={setSelected} />

        <MyPerformance />

        <StatCards stats={stats} range={range} setRange={setRange} />

        <div>
          <div className="flex flex-wrap gap-1 border-b-2 border-black" data-testid="ticket-tabs">
            <button className={tabCls(tab === "all")} onClick={() => setTab("all")} data-testid="tab-all-tickets">
              All Leads {stats?.unassigned > 0 && <span className="ml-1 rounded-full bg-[#FACC15] px-2 py-0.5 text-xs" data-testid="unassigned-count">{stats.unassigned} unassigned</span>}
            </button>
            <button className={tabCls(tab === "mine")} onClick={() => setTab("mine")} data-testid="tab-my-tickets">
              My Leads {stats?.my_open > 0 && <span className="ml-1 rounded-full bg-black px-2 py-0.5 text-xs text-white">{stats.my_open}</span>}
            </button>
            <button className={tabCls(tab === "offline")} onClick={() => setTab("offline")} data-testid="tab-offline-leads">
              Offline / Sales Leads
            </button>
          </div>
          {tab === "offline" ? (
            <div className="rounded-b-xl border-2 border-t-0 border-black bg-white/60 p-4"><OfflineLeads products={config?.products || []} /></div>
          ) : (
          <div className="space-y-4 rounded-b-xl border-2 border-t-0 border-black bg-white/60 p-4">
            <div className="sticky top-[76px] z-20 -mx-4 -mt-4 rounded-b-lg border-b-2 border-dashed border-zinc-300 bg-[#FFFBEB]/95 px-4 pb-3 pt-4 backdrop-blur" data-testid="sticky-filters">
              <Filters filters={filters} setFilters={setFilters} products={config?.products || []} onExport={exportCsv} exporting={exporting} />
            </div>
            {ticketNo && exact && (
              <div className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-black bg-[#FACC15] px-4 py-3 fade-up" data-testid="ticket-360-banner">
                <span className="font-mono text-xl font-extrabold">{exact.ticket_id}</span>
                <span className="text-sm font-semibold">{exact.name} · {exact.product} · {exact.query}</span>
                <span className="pill border-black bg-white">{exact.status}</span>
                <span className="text-sm">{exact.assigned_to ? `with ${exact.assigned_to}` : "unassigned"}</span>
                <button className="btn-dark ml-auto !py-2 !text-sm" onClick={() => setSelected(exact)} data-testid="ticket-360-open-button">Open full ticket view</button>
              </div>
            )}
            {ticketNo && exactMissing && <p className="text-sm font-semibold text-[#B91C1C]" data-testid="ticket-360-missing">No ticket matches {ticketNo.toUpperCase()}.</p>}
            <p className="flex flex-wrap items-center gap-3 text-sm font-semibold text-zinc-600" data-testid="ticket-count">
              <span>{pageData?.total ?? 0} lead{pageData?.total === 1 ? "" : "s"}{pageData?.pages > 1 ? ` · page ${page} of ${pageData.pages}` : ""}</span>
              {isAdmin && tab === "all" && <span className="ml-auto"><DistributeButton unassigned={stats?.unassigned || 0} /></span>}
            </p>
            <TicketTable
              tickets={tickets}
              user={user}
              onOpen={setSelected}
              onAssign={(t) => assign.mutate(t)}
              loading={isLoading && !pageData}
              selectable={isAdmin && tab === "all"}
              selected={picked}
              onToggle={togglePick}
              onToggleAll={toggleAll}
            />
            {pageData && <Pagination page={page} pages={pageData.pages} total={pageData.total} pageSize={PAGE_SIZE} onPage={setPage} />}
          </div>
          )}
        </div>
        {isAdmin && <BulkAssignBar selected={picked} onClear={() => setPicked(new Set())} />}
      </main>
      <TicketDrawer ticket={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
